/**
 * @namespace validationUtils
 * @description Una colección de funciones de utilidad para la validación.
 */
export const validationUtils = {
  /**
   * @function isValidEmail
   * @description Comprueba si una cadena es una dirección de correo electrónico válida.
   * @param {string} email - La dirección de correo electrónico a validar.
   * @returns {boolean} - Verdadero si el correo electrónico es válido, falso en caso contrario.
   */
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  isValidPhone: (phone: string): boolean => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone);
  },

  isValidPassword: (password: string): boolean => {
    return password.length >= 8;
  },

  isValidName: (name: string): boolean => {
    return name.length > 0 && name.length <= 100;
  },

  isValidDescription: (description: string): boolean => {
    return description.length <= 500;
  },

  /**
   * @function isValidRFC
   * @description Valida el formato del RFC mexicano (persona física o moral).
   * @param {string} rfc - El RFC a validar.
   * @returns {boolean} - Verdadero si el RFC tiene formato válido.
   */
  isValidRFC: (rfc: string): boolean => {
    if (!rfc || !rfc.trim()) return false;

    const rfcClean = rfc.trim().toUpperCase().replace(/\s+/g, '');

    // Persona física: 13 caracteres (4 letras + 6 dígitos + 3 alfanuméricos)
    // Persona moral: 12 caracteres (3 letras + 6 dígitos + 3 alfanuméricos)
    const rfcPattern = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;

    return (rfcClean.length === 12 || rfcClean.length === 13) && rfcPattern.test(rfcClean);
  },

  /**
   * @function validateRFC
   * @description Valida el RFC y retorna mensaje de error si es inválido.
   * @param {string} rfc - El RFC a validar.
   * @param {'fisica' | 'moral' | 'both'} [type='both'] - Tipo de RFC a validar.
   * @returns {string | null} - Mensaje de error o null si es válido.
   */
  validateRFC: (rfc: string, type: 'fisica' | 'moral' | 'both' = 'both'): string | null => {
    if (!rfc || !rfc.trim()) {
      return 'El RFC es obligatorio';
    }

    const rfcClean = rfc.trim().toUpperCase().replace(/\s+/g, '');

    if (type === 'fisica') {
      if (rfcClean.length !== 13) {
        return 'El RFC de persona física debe tener 13 caracteres';
      }
      const pattern = /^[A-ZÑ&]{4}[0-9]{6}[A-Z0-9]{3}$/;
      if (!pattern.test(rfcClean)) {
        return 'El RFC debe tener el formato: 4 letras + 6 dígitos + 3 caracteres alfanuméricos';
      }
    } else if (type === 'moral') {
      if (rfcClean.length !== 12) {
        return 'El RFC de persona moral debe tener 12 caracteres';
      }
      const pattern = /^[A-ZÑ&]{3}[0-9]{6}[A-Z0-9]{3}$/;
      if (!pattern.test(rfcClean)) {
        return 'El RFC debe tener el formato: 3 letras + 6 dígitos + 3 caracteres alfanuméricos';
      }
    } else {
      // Validar ambos tipos
      if (rfcClean.length !== 12 && rfcClean.length !== 13) {
        return 'El RFC debe tener 12 o 13 caracteres';
      }
      const pattern = /^([A-ZÑ&]{3}|[A-ZÑ&]{4})[0-9]{6}[A-Z0-9]{3}$/;
      if (!pattern.test(rfcClean)) {
        return 'El RFC debe tener el formato: 3-4 letras + 6 dígitos + 3 caracteres alfanuméricos';
      }
    }

    return null;
  },

  /**
   * @function getRFCType
   * @description Determina si un RFC es de persona física o moral basándose en su longitud.
   * @param {string} rfc - El RFC a analizar.
   * @returns {'fisica' | 'moral' | null} - Tipo de RFC o null si no es válido.
   */
  getRFCType: (rfc: string): 'fisica' | 'moral' | null => {
    if (!rfc || !rfc.trim()) return null;

    const rfcClean = rfc.trim().toUpperCase().replace(/\s+/g, '');

    if (rfcClean.length === 13) {
      return 'fisica';
    } else if (rfcClean.length === 12) {
      return 'moral';
    }

    return null;
  },

  /**
   * @function formatRFCInput
   * @description Filtra y formatea el RFC mientras se escribe, permitiendo solo caracteres válidos según la posición.
   * Soporta tanto RFC de persona moral (3 letras) como persona física (4 letras).
   * @param {string} value - El valor del RFC a formatear.
   * @returns {string} - El RFC formateado con solo caracteres válidos.
   */
  formatRFCInput: (value: string): string => {
    if (!value) return '';

    let formatted = value.toUpperCase();

    // Limitar a 13 caracteres máximo
    if (formatted.length > 13) {
      formatted = formatted.substring(0, 13);
    }

    // Detectar si es persona moral (3 letras) o física (4 letras)
    // Si en la posición 3 hay un número, entonces son 3 letras iniciales
    const esPersonaMoral = formatted.length > 3 && /[0-9]/.test(formatted[3]);
    const numLetrasIniciales = esPersonaMoral ? 3 : 4;

    // Filtrar caracteres según la posición
    formatted = formatted
      .split('')
      .filter((char, i) => {
        // Primeras 4 posiciones: permitir letras A-Z, Ñ, &
        // En la posición 3, también permitir números para detectar si es persona física o moral
        if (i < 4) {
          if (i === 3) {
            // En posición 3, permitir letras o números (para detectar 3 o 4 letras)
            return /[A-ZÑ&0-9]/.test(char);
          }
          return /[A-ZÑ&]/.test(char);
        }

        // Determinar dónde empiezan los números según el tipo de RFC
        const inicioNumeros = numLetrasIniciales;
        const finNumeros = inicioNumeros + 6;
        const inicioAlfanumericos = finNumeros;

        // Zona de números (6 dígitos después de las letras)
        if (i >= inicioNumeros && i < finNumeros) {
          return /[0-9]/.test(char);
        }

        // Zona de caracteres alfanuméricos (últimas 3 posiciones)
        if (i >= inicioAlfanumericos && i < 13) {
          return /[A-Z0-9]/.test(char);
        }

        return false;
      })
      .join('');

    return formatted;
  },
};

/**
 * @namespace formatUtils
 * @description Una colección de funciones de utilidad para formatear datos.
 */
export const formatUtils = {
  /**
   * @function formatCurrency
   * @description Formatea un número como una cadena de moneda.
   * @param {number} amount - La cantidad a formatear.
   * @param {string} [currency='USD'] - El código de la moneda.
   * @returns {string} - La cadena de moneda formateada.
   */
  formatCurrency: (amount: number, currency = 'USD'): string => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  },

  formatDate: (date: Date | string, locale = 'es-ES'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dateObj);
  },

  formatDateTime: (date: Date | string, locale = 'es-ES'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  },

  formatRelativeTime: (date: Date | string, locale = 'es-ES'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - dateObj.getTime()) / 1000
    );

    if (diffInSeconds < 60) return 'hace un momento';
    if (diffInSeconds < 3600)
      return `hace ${Math.floor(diffInSeconds / 60)} minutos`;
    if (diffInSeconds < 86400)
      return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
    if (diffInSeconds < 2592000)
      return `hace ${Math.floor(diffInSeconds / 86400)} días`;

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dateObj);
  },

  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
};

/**
 * @namespace stringUtils
 * @description Una colección de funciones de utilidad para manipular cadenas.
 */
export const stringUtils = {
  /**
   * @function capitalize
   * @description Pone en mayúscula la primera letra de una cadena.
   * @param {string} str - La cadena a poner en mayúscula.
   * @returns {string} - La cadena en mayúscula.
   */
  capitalize: (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  capitalizeWords: (str: string): string => {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  },

  slugify: (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  },

  truncate: (str: string, length: number, suffix = '...'): string => {
    if (str.length <= length) return str;
    return str.substring(0, length) + suffix;
  },

  generateId: (prefix = ''): string => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return prefix
      ? `${prefix}_${timestamp}_${randomStr}`
      : `${timestamp}_${randomStr}`;
  },
};

/**
 * @namespace arrayUtils
 * @description Una colección de funciones de utilidad para manipular arrays.
 */
export const arrayUtils = {
  /**
   * @function groupBy
   * @description Agrupa los elementos de un array en función de una clave dada.
   * @template T
   * @param {T[]} array - El array a agrupar.
   * @param {keyof T} key - La clave por la que agrupar.
   * @returns {Record<string, T[]>} - Un objeto con los elementos agrupados.
   */
  groupBy: <T>(array: T[], key: keyof T): Record<string, T[]> => {
    return array.reduce((groups, item) => {
      const group = String(item[key]);
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    }, {} as Record<string, T[]>);
  },

  sortBy: <T>(
    array: T[],
    key: keyof T,
    direction: 'asc' | 'desc' = 'asc'
  ): T[] => {
    return [...array].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  },

  unique: <T>(array: T[]): T[] => {
    return Array.from(new Set(array));
  },

  uniqueBy: <T>(array: T[], key: keyof T): T[] => {
    const seen = new Set();
    return array.filter((item) => {
      const value = item[key];
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  },

  chunk: <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },
};

/**
 * @namespace objectUtils
 * @description Una colección de funciones de utilidad para manipular objetos.
 */
export const objectUtils = {
  /**
   * @function deepClone
   * @description Crea un clon profundo de un objeto.
   * @template T
   * @param {T} obj - El objeto a clonar.
   * @returns {T} - El objeto clonado.
   */
  deepClone: <T>(obj: T): T => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
    if (obj instanceof Array)
      return obj.map((item) => objectUtils.deepClone(item)) as unknown as T;
    if (typeof obj === 'object') {
      const clonedObj = {} as T;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          clonedObj[key] = objectUtils.deepClone(obj[key]);
        }
      }
      return clonedObj;
    }
    return obj;
  },

  pick: <T extends object, K extends keyof T>(
    obj: T,
    keys: K[]
  ): Pick<T, K> => {
    const result = {} as Pick<T, K>;
    keys.forEach((key) => {
      if (key in obj) {
        result[key] = obj[key];
      }
    });
    return result;
  },

  omit: <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    const result = { ...obj };
    keys.forEach((key) => {
      delete result[key];
    });
    return result;
  },

  isEmpty: (obj: any): boolean => {
    if (obj == null) return true;
    if (Array.isArray(obj) || typeof obj === 'string') return obj.length === 0;
    if (typeof obj === 'object') return Object.keys(obj).length === 0;
    return false;
  },
};

/**
 * @namespace storageUtils
 * @description Una colección de funciones de utilidad para interactuar con localStorage.
 */
export const storageUtils = {
  /**
   * @function set
   * @description Guarda un valor en localStorage.
   * @param {string} key - La clave bajo la cual guardar el valor.
   * @param {*} value - El valor a guardar.
   */
  set: (key: string, value: any): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  get: <T>(key: string, defaultValue?: T): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue || null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue || null;
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },
};

/**
 * @namespace urlUtils
 * @description Una colección de funciones de utilidad para trabajar con URLs.
 */
export const urlUtils = {
  /**
   * @function buildQueryString
   * @description Construye una cadena de consulta de URL a partir de un objeto de parámetros.
   * @param {Record<string, any>} params - Los parámetros a incluir en la cadena de consulta.
   * @returns {string} - La cadena de consulta generada.
   */
  buildQueryString: (params: Record<string, any>): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    return searchParams.toString();
  },

  parseQueryString: (queryString: string): Record<string, string> => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  },

  isValidUrl: (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },
};

/**
 * @namespace timingUtils
 * @description Una colección de funciones de utilidad para estabilizar y limitar las llamadas a funciones.
 */
export const timingUtils = {
  /**
   * @function debounce
   * @description Crea una función estabilizada que retrasa la invocación de `func` hasta que hayan transcurrido `wait` milisegundos desde la última vez que se invocó la función estabilizada.
   * @template T
   * @param {T} func - La función a estabilizar.
   * @param {number} wait - El número de milisegundos a retrasar.
   * @returns {(...args: Parameters<T>) => void} - La nueva función estabilizada.
   */
  debounce: <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  throttle: <T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },
};
